import { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { ConfigFeatureToggles, RootState } from "../../../models";
import { getFeatureToggles } from "../app/state/selectors";
import { useQueryParam } from "../components/hooks";

// Hook to check a feature toggle value with optional query parameter override.
export function useFeatureToggleWithQueryParam(
    toggleName: keyof ConfigFeatureToggles,
    queryParamName?: string,
): boolean {
    const paramName = queryParamName ?? toggleName;
    const isEnabledByQueryParam = useQueryParam(paramName) === "true";
    const isEnabledByToggle = useSelector((state: RootState) => !!getFeatureToggles(state)[toggleName]);

    return isEnabledByQueryParam || isEnabledByToggle;
}

const initialWidth = 1200;

// Hook will keep track of if cart at any point is empty
export function useEmptyCart(ids: string[]) {
    const [isCartEmpty, setIsCartEmpty] = useState(false);
    const stringifyCartIds = JSON.stringify(ids);

    useEffect(() => {
        const innerIds = JSON.parse(stringifyCartIds);
        if (innerIds.length === 0) {
            setIsCartEmpty(true);
        }
    }, [setIsCartEmpty, stringifyCartIds]);

    return isCartEmpty;
}

export function usePrevious<T>(value: T) {
    const ref = useRef<T>();
    useEffect(() => {
        ref.current = value;
    });
    return ref.current;
}

export const useViewport = (): { isDesktopVersion: boolean; width: number } => {
    const [width, setWidth] = useState(initialWidth);
    const [isDesktopVersion, setIsDesktopVersion] = useState(false);

    const updateFields = (): void => {
        setWidth(window.innerWidth);
        setIsDesktopVersion(window.innerWidth >= initialWidth - 25);
    };

    useEffect(() => {
        updateFields();
        window.addEventListener("resize", updateFields);
        return (): void => window.removeEventListener("resize", updateFields);
    }, []);

    return { width, isDesktopVersion };
};
